import { useState, useCallback } from 'react'

import {
  ModerationError,
  ModerationSeverity,
  processModerationError,
} from './moderationUtils'

export interface UseModerationErrorReturn {
  /** Current moderation error, or null if none */
  moderationError: ModerationError | null
  /** Set the moderation error directly */
  setModerationError: (error: ModerationError | null) => void
  /**
   * Process errorInfo from a subscription payload.
   * Returns true if it was a moderation error (caller should skip normal error handling).
   * Returns false if not a moderation error (caller should continue with normal error handling).
   */
  handleErrorInfo: (
    errorInfo: Array<{ code: string; message: string }> | undefined | null,
    onModerationError?: (categories: string[], severity: ModerationSeverity) => void,
  ) => boolean
  /** Clear the current moderation error */
  clearModerationError: () => void
}

/**
 * Shared React hook for moderation error state management.
 * Used by both useOracleChatSubscription and useGotsChatSubscription.
 *
 * This hook centralizes all moderation error handling logic so both chat systems
 * (OraclePage and OracleSideGuide) behave identically.
 *
 * @example
 * ```typescript
 * const { moderationError, handleErrorInfo, clearModerationError } = useModerationError()
 *
 * // In subscription onData handler:
 * if (payload.errorInfo?.length > 0) {
 *   if (handleErrorInfo(payload.errorInfo, onModerationError)) {
 *     // Was moderation error - keep pendingMessage visible, skip normal error handling
 *     return
 *   }
 *   // Continue with normal error handling...
 * }
 * ```
 */
export const useModerationError = (): UseModerationErrorReturn => {
  const [moderationError, setModerationError] = useState<ModerationError | null>(null)

  const handleErrorInfo = useCallback(
    (
      errorInfo: Array<{ code: string; message: string }> | undefined | null,
      onModerationError?: (categories: string[], severity: ModerationSeverity) => void,
    ): boolean => {
      const error = processModerationError(errorInfo)

      if (error) {
        setModerationError(error)

        // Fire callback if provided
        if (onModerationError) {
          onModerationError(error.categories, error.config.severity)
        }

        return true // Was a moderation error
      }

      return false // Not a moderation error
    },
    [],
  )

  const clearModerationError = useCallback(() => {
    setModerationError(null)
  }, [])

  return {
    moderationError,
    setModerationError,
    handleErrorInfo,
    clearModerationError,
  }
}
